// Pending registrations (UC-PATIENTS-024; PAT-64, PAT-68, PAT-70; D42): what a patient typed on the kiosk waits here until
// staff check it. Today shows the active branch's queue; /patients/registrations lists every status; a patient record names its
// own waiting update. The review shows the submission as typed with its duplicate matches and the images the kiosk captured
// (signed links, BR-PATIENTS-033), and approves it — a duplicate decision (a typed reason past an exact ID match, an
// acknowledgement of a probable one, or applying it to a matched patient) and any corrections the full patient rules ask for
// travel with the approval — or rejects it with a reason. Every approval and rejection carries an Idempotency-Key; a changed
// body is a new key.
import { CONSENT_PURPOSE_LABELS, type ConsentPurpose, type DuplicateMatch, type RegistrationApproveInput, type RegistrationCaptureSlot, type RegistrationRecordChanges, type RegistrationRecordField, type RegistrationSubmissionView } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { reference, registrations } from "../api/endpoints.patients";
import { describeError, isApiError } from "../api/problem";
import { formatDate, formatDateTime, formatTime } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Checkbox, Chip, Empty, ErrorAlert, Field, KV, Loading, Modal, Pager, Panel, Select, TextArea, TextInput } from "../ui/bits";
import { NoAccessPanel, NoActiveBranch, PageHead } from "./common";

type Submission = RegistrationSubmissionView;
const REVIEW = "patients.registration.review";
const STATUSES = ["Pending", "Approved", "Rejected"] as const;
type QueueStatus = (typeof STATUSES)[number];
const RULE_LABEL: Record<DuplicateMatch["rule"], string> = { ExactId: "same ID number", SurnameDob: "same surname and date of birth", SurnameMobile: "same surname and mobile", FirstNamesSurname: "same names" };
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v : null);
const dash = <span className="faint">—</span>;

/** The name the patient typed, as the queue shows it. */
export function submissionName(s: Submission): string {
  const d = s.payload.details;
  return [str(d.firstNames), str(d.surname)].filter(Boolean).join(" ") || "Unnamed";
}
const kindLabel = (s: Submission): string => (s.kind === "NewPatient" ? "New patient" : "Update");

function invalidate(qc: ReturnType<typeof useQueryClient>, patientId?: string | null) {
  void qc.invalidateQueries({ queryKey: ["registrations"] });
  void qc.invalidateQueries({ queryKey: ["patients"] });
  if (patientId) void qc.invalidateQueries({ queryKey: ["patient", patientId] });
}

// --- Today's panel ---------------------------------------------------------------------------------------------------------------

/** Today's Pending registrations for the active branch, oldest first; a reviewer opens each one from here. */
export function PendingRegistrationsPanel({ onNotice }: { onNotice: (n: ReactNode) => void }) {
  const s = useSessionView();
  const branchId = s?.activeBranchId ?? undefined;
  const q = useQuery({ queryKey: ["registrations", "Pending", branchId ?? ""], queryFn: () => registrations.list({ status: "Pending", branchId }, { limit: 5 }), enabled: can(s, REVIEW) && !!branchId, refetchInterval: 60_000 });
  const [open, setOpen] = useState<Submission | null>(null);
  // Without an active branch Today already says so; the list needs a branch, and asked for nothing it would show Loading for
  // good (review, 4 Oct 2026).
  if (!can(s, REVIEW) || !branchId) return null;
  const items = q.data?.data.items ?? [];
  const more = !!q.data?.data.nextCursor;
  return (
    <Panel title="Pending registrations" meta={q.data ? `${items.length}${more ? "+" : ""} waiting` : "from the kiosk"} tight
      head={<Link className="btn sm right" to="/patients/registrations">All registrations</Link>}>
      {q.isPending && <Loading what="registrations" />}
      {q.isError && <ErrorAlert error={q.error} />}
      {q.data && items.length === 0 && <Empty title="No registrations waiting.">What a patient sends from the kiosk waits here for checking.</Empty>}
      {items.length > 0 && (
        <div className="list">
          {items.map((r) => (
            <div key={r.id} className="item">
              <span className="t">{formatTime(r.submittedAt)}</span>
              <div className="grow">
                <div className="primary">{submissionName(r)}</div>
                <div className="sub">{kindLabel(r)}{r.duplicateMatches.length > 0 ? ` · ${r.duplicateMatches.length} possible match${r.duplicateMatches.length === 1 ? "" : "es"}` : ""}</div>
              </div>
              <button className="btn sm" type="button" onClick={() => setOpen(r)}>Review</button>
            </div>
          ))}
        </div>
      )}
      {open && <RegistrationReview sub={open} onClose={() => setOpen(null)} onDone={(n) => { setOpen(null); onNotice(n); }} />}
    </Panel>
  );
}

// --- the patient record's notice ---------------------------------------------------------------------------------------------------

/** A patient's own waiting kiosk update (UC-PATIENTS-024 A5), with the review one click away. */
export function PendingSubmissionNotice({ patientId, onNotice }: { patientId: string; onNotice: (n: ReactNode) => void }) {
  const s = useSessionView();
  const q = useQuery({ queryKey: ["registrations", "Pending", "patient", patientId], queryFn: () => registrations.list({ status: "Pending", existingPatientId: patientId }, { limit: 5 }), enabled: can(s, REVIEW) });
  const [open, setOpen] = useState<Submission | null>(null);
  const items = q.data?.data.items ?? [];
  if (!can(s, REVIEW) || items.length === 0) return null;
  const first = items[0]!;
  return (
    <>
      <Alert tone="info" actions={<button className="btn sm" type="button" onClick={() => setOpen(first)}>Review</button>}>
        <b>An update from the kiosk is waiting for review</b> — sent {formatDateTime(first.submittedAt)}{first.openedBy ? `, handed over by ${first.openedBy.displayName}` : ""}.{items.length > 1 ? ` ${items.length - 1} more after it.` : ""}
      </Alert>
      {open && <RegistrationReview sub={open} onClose={() => setOpen(null)} onDone={(n) => { setOpen(null); onNotice(n); }} />}
    </>
  );
}

// --- the queue page --------------------------------------------------------------------------------------------------------------

/** /patients/registrations: the queue by status for the active branch, a page at a time — Pending oldest first, a reviewed status newest first (the API's order). */
export function RegistrationsQueue() {
  const s = useSessionView();
  const [params] = useSearchParams();
  const status: QueueStatus = (STATUSES as readonly string[]).includes(params.get("status") ?? "") ? (params.get("status") as QueueStatus) : "Pending";
  // The pages walked so far, for the status shown: another status starts at its first page.
  const [paging, setPaging] = useState<{ status: QueueStatus; cursors: (string | undefined)[] }>({ status, cursors: [undefined] });
  const cursors = paging.status === status ? paging.cursors : [undefined];
  const setCursors = (f: (c: (string | undefined)[]) => (string | undefined)[]) => setPaging({ status, cursors: f(cursors) });
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const [open, setOpen] = useState<Submission | null>(null);
  const branchId = s?.activeBranchId ?? undefined;
  const cursor = cursors.at(-1);
  const q = useQuery({ queryKey: ["registrations", status, branchId ?? "", cursor ?? ""], queryFn: () => registrations.list({ status, branchId }, { limit: 50, cursor }), enabled: !!branchId });
  const denied = q.isError && isApiError(q.error) && q.error.code === "PERMISSION_DENIED";
  const items = q.data?.data.items ?? [];
  return (
    <section className="screen" id="s-registrations">
      <div className="content">
        <PageHead title="Pending registrations" sub="What patients sent from the kiosk, for staff to check before it becomes the record" />
        {notice}
        <nav className="tabs" aria-label="Status">
          {STATUSES.map((st) => <Link key={st} to={st === "Pending" ? "/patients/registrations" : `/patients/registrations?status=${st}`} className={st === status ? "active" : ""} aria-current={st === status ? "page" : undefined}>{st}</Link>)}
        </nav>
        {!branchId ? <NoActiveBranch what="its registrations" /> : denied ? <NoAccessPanel page="Pending registrations" error={q.error} /> : (
          <Panel tight>
            {q.isPending && <Loading what="registrations" />}
            {q.isError && <ErrorAlert error={q.error} />}
            {q.data && items.length === 0 && <Empty title={status === "Pending" ? "No registrations waiting." : `No ${status.toLowerCase()} registrations.`}>{status === "Pending" ? "A patient's kiosk submission appears here until it is approved or rejected." : "Reviewed submissions are listed here with who reviewed them."}</Empty>}
            {items.length > 0 && (
              <div className="tbl-scroll">
                <table>
                  <thead><tr><th>Sent</th><th>Patient</th><th>Kind</th><th>Handed over by</th><th>Matches</th>{status !== "Pending" && <th>Reviewed</th>}<th></th></tr></thead>
                  <tbody>
                    {items.map((r) => (
                      <tr key={r.id}>
                        <td className="mono">{formatDateTime(r.submittedAt)}</td>
                        <td>{r.resultingPatientId ? <Link to={`/patients/${r.resultingPatientId}`}>{submissionName(r)}</Link> : r.existingPatientId ? <Link to={`/patients/${r.existingPatientId}`}>{submissionName(r)}</Link> : submissionName(r)}</td>
                        <td>{kindLabel(r)}</td>
                        <td>{r.openedBy?.displayName ?? dash}</td>
                        <td>{r.duplicateMatches.length || dash}</td>
                        {status !== "Pending" && <td>{r.reviewedBy ? `${r.reviewedBy.displayName}, ${formatDateTime(r.reviewedAt)}` : dash}{r.rejectionReason && <div className="small faint">{r.rejectionReason}</div>}</td>}
                        <td className="r">{r.status === "Pending" && can(s, REVIEW) && <button className="btn sm" type="button" onClick={() => setOpen(r)}>Review</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {(cursors.length > 1 || q.data?.data.nextCursor) && (
              <div className="panel-foot">
                <Pager label={`Page ${cursors.length}`} hasPrev={cursors.length > 1} hasNext={!!q.data?.data.nextCursor}
                  onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, q.data!.data.nextCursor])} />
              </div>
            )}
          </Panel>
        )}
        {open && <RegistrationReview sub={open} onClose={() => setOpen(null)} onDone={(n) => { setOpen(null); setNotice(n); }} />}
      </div>
    </section>
  );
}

// --- the review --------------------------------------------------------------------------------------------------------------------

/** `details.address.line1` → `{ details: { address: { line1: v } } }`, merged into what is already corrected. */
function setPath(target: Record<string, unknown>, path: string, value: string): Record<string, unknown> {
  const [head, ...rest] = path.split(".");
  if (!head) return target;
  if (rest.length === 0) return { ...target, [head]: value };
  const inner = (target[head] && typeof target[head] === "object" ? target[head] : {}) as Record<string, unknown>;
  return { ...target, [head]: setPath(inner, rest.join("."), value) };
}
/** The medical-aid fields a reviewer resolves from the reference lists — never by typing an internal id into a box. */
const PICKED = /^medicalAid\.(schemeId|planId|optionId)$/;
const correctable = (path: string): boolean => /^(details|medicalAid)\.[A-Za-z0-9_.]+$/.test(path) && !PICKED.test(path);
const fieldLabel = (path: string): string => path.replace(/^details\./, "").replace(/^medicalAid\./, "medical aid ").replace(/\./g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
const NO_PICK = { schemeId: "", planId: "", optionId: "" };

interface Refusal { errors: { path: string; message: string }[]; duplicates: DuplicateMatch[] | null }

const CAPTURE_LABEL: Record<RegistrationCaptureSlot, string> = {
  idDocument: "ID document", patientPhoto: "Photo", driversLicence: "Driver's licence", cardFront: "Medical-aid card, front", cardBack: "Medical-aid card, back", signature: "Signature",
};

/**
 * One submission as its review reads it (`GET /registration-submissions/{id}`): the image links and the changes made on the
 * record since the kiosk read it. Each opening of the review asks afresh — the read is audited, so it is never repeated in the
 * background.
 */
const useSubmissionDetail = (id: string, enabled: boolean) =>
  useQuery({ queryKey: ["registration", id], queryFn: () => registrations.get(id), enabled, staleTime: Infinity, gcTime: 0, retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false });
type DetailQuery = ReturnType<typeof useSubmissionDetail>;

/**
 * The images the kiosk captured, for the reviewer to check before approving (UC-PATIENTS-024 step 2; BR-PATIENTS-033): signed
 * links that are hers alone and lapse after 15 minutes. Once they lapse the images already shown stay and a button asks for new
 * links.
 */
function CapturedImages({ q }: { q: DetailQuery }) {
  const links = q.data?.data.captureLinks ?? [];
  const expiresAt = links[0]?.expiresAt ?? null;
  const [expired, setExpired] = useState<string | null>(null);
  useEffect(() => {
    if (!expiresAt) return;
    const wait = Math.max(0, Date.parse(expiresAt) - Date.now());
    // A delay past setTimeout's 32-bit limit would fire at once; no link lives that long (they last 15 minutes).
    if (wait > 2 ** 31 - 1) return;
    const t = setTimeout(() => setExpired(expiresAt), wait);
    return () => clearTimeout(t);
  }, [expiresAt]);
  const lapsed = expiresAt !== null && expired === expiresAt;
  return (
    <section className="stack gap-8" aria-label="Captured on the kiosk">
      <div className="small strong muted">Captured on the kiosk</div>
      {q.isPending && <Loading what="the images" />}
      {q.isError && <ErrorAlert error={q.error} />}
      {q.isSuccess && links.length === 0 && <span className="help">No images were captured.</span>}
      {links.length > 0 && (
        <div className="captures">
          {links.map((l) => (
            <figure key={l.slot}>
              {l.contentType.startsWith("image/")
                ? <img src={l.url} alt={CAPTURE_LABEL[l.slot]} width={l.widthPx ?? undefined} height={l.heightPx ?? undefined} loading="lazy" />
                : <div className="doc">PDF · {Math.max(1, Math.round(l.sizeBytes / 1024))} KB</div>}
              <figcaption>
                <span>{CAPTURE_LABEL[l.slot]}</span>
                {!lapsed && <a href={l.url} target="_blank" rel="noopener" aria-label={`Open ${CAPTURE_LABEL[l.slot]} full size`}>Open</a>}
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      {lapsed && (
        <Alert tone="info" actions={<button className="btn sm" type="button" onClick={() => void q.refetch()} disabled={q.isFetching}>Show the images again</button>}>
          The links to these images have lapsed (they last 15 minutes).
        </Alert>
      )}
    </section>
  );
}

const RECORD_FIELD_LABEL: Record<RegistrationRecordField, string> = {
  "details.title": "Title", "details.initials": "Initials", "details.firstNames": "First names", "details.surname": "Surname", "details.knownAs": "Known as",
  "details.idNumber": "ID", "details.dateOfBirth": "Date of birth", "details.gender": "Gender", "details.mobile": "Mobile", "details.email": "Email",
  "details.altPhone": "Other phone", "details.preferredChannel": "Preferred channel", "details.preferredLanguage": "Preferred language",
  "details.noElectronicContact": "No electronic contact", "details.useMainAccountContact": "Uses the account holder's contact details",
  "details.address": "Address", "details.postalAddress": "Postal address", "details.medicalHistory": "Medical history", medicalAid: "Medical aid",
};
type Decision = "keep" | "take";

/**
 * What staff changed on the record after the kiosk read it, that approving would overwrite (UC-PATIENTS-024 A9; BR-PATIENTS-032):
 * each change with its value as the kiosk found it, on the record now and as the patient sent it, and the reviewer's choice —
 * keep the record's or take the patient's. Approve waits until each is decided.
 */
function RecordChanges({ name, changes, decisions, onDecide }: { name: string; changes: RegistrationRecordChanges; decisions: Partial<Record<RegistrationRecordField, Decision>>; onDecide: (f: RegistrationRecordField, d: Decision) => void }) {
  // A value the kiosk read that was not kept: changed by staff while the patient was at the kiosk, or — for a submission whose
  // handover kept no digests — not known at all, and then who changed what cannot be told (testing of 12 Oct 2026).
  const asRead = (c: RegistrationRecordChanges["changes"][number]): ReactNode =>
    c.redacted ? <span className="faint">not shown to your role</span> : c.atSubmissionKept ? c.atSubmission ?? dash : <span className="faint">{c.changeKnown ? "changed while the patient was at the kiosk" : "not known"}</span>;
  const value = (v: string | null, redacted: boolean): ReactNode => (redacted ? <span className="faint">not shown to your role</span> : v ?? dash);
  const unknown = changes.changes.some((c) => !c.changeKnown);
  return (
    <fieldset className="stack gap-8 record-changes">
      <legend className="small strong muted">The record changed after {name} sent this</legend>
      {unknown
        ? <Alert tone="warn"><b>The record changed while {name} was at the kiosk, and Vision X cannot tell who changed what.</b> Some of these may be {name}'s own changes: check each with them before keeping the record's value.</Alert>
        : <span className="help">Someone changed these on the record after the kiosk read it. Approving would write over them: keep the record's value or take the patient's, for each.</span>}
      <div className="tbl-scroll">
        <table>
          <thead><tr><th>Field</th><th>When the kiosk read it</th><th>On the record now</th><th>The patient sent</th><th>Decision</th></tr></thead>
          <tbody>
            {changes.changes.map((c) => {
              const label = RECORD_FIELD_LABEL[c.field];
              return (
                <tr key={c.field}>
                  <td>{label}</td>
                  <td>{asRead(c)}</td>
                  <td>{value(c.onRecord, c.redacted)}</td>
                  <td>{value(c.submitted, c.redacted)}</td>
                  <td>
                    <div className="stack gap-4" role="radiogroup" aria-label={`${label}: keep or take`}>
                      {(["keep", "take"] as const).map((d) => (
                        <label key={d} className="check">
                          <input type="radio" name={`rc-${c.field}`} checked={decisions[c.field] === d} onChange={() => onDecide(c.field, d)} aria-label={`${d === "keep" ? "Keep the record's" : "Take the patient's"} ${label === "ID" ? label : label.toLowerCase()}`} />
                          <span className={`box radio${decisions[c.field] === d ? " on" : ""}`} />{d === "keep" ? "Keep the record's" : "Take the patient's"}
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </fieldset>
  );
}

/**
 * The review of one submission: what the patient typed, the duplicate decision, Approve and Reject. A refusal of the full
 * patient rules names the field; each correctable field gets its own box and the corrections go with the next approval.
 */
export function RegistrationReview({ sub, onClose, onDone }: { sub: Submission; onClose: () => void; onDone: (n: ReactNode) => void }) {
  const qc = useQueryClient();
  const block = useWriteBlock();
  const [target, setTarget] = useState<"new" | string>("new");
  const [reason, setReason] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [corrections, setCorrections] = useState<Record<string, string>>({});
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const [failure, setFailure] = useState<ReactNode | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  // The scheme, plan and option chosen from the reference lists for a scheme the patient typed (UC-PATIENTS-024 A8).
  const [pick, setPick] = useState(NO_PICK);
  // UC-PATIENTS-024 A9: what staff changed on the record since the kiosk read it — as the review read it, or as a refusal listed it
  // since — and the reviewer's decision on each.
  const detail = useSubmissionDetail(sub.id, sub.status === "Pending");
  const [listed, setListed] = useState<RegistrationRecordChanges | null | undefined>(undefined);
  // Decisions hold for the record's version they were made on: a list read again at another version asks afresh.
  const [decided, setDecided] = useState<{ version: number; by: Partial<Record<RegistrationRecordField, Decision>> } | null>(null);
  const recordChanges = listed !== undefined ? listed : detail.data?.data.recordChanges ?? null;
  const decisions = recordChanges && decided?.version === recordChanges.patientVersion ? decided.by : {};
  const decide = (f: RegistrationRecordField, d: Decision) => {
    if (recordChanges) setDecided({ version: recordChanges.patientVersion, by: { ...decisions, [f]: d } });
  };
  const decidedAll = !recordChanges || recordChanges.changes.every((c) => decisions[c.field] !== undefined);
  // One Idempotency-Key per distinct body: a retry of the same approval replays; a changed decision is a new request.
  const keys = useRef(new Map<string, string>());
  const keyFor = (body: unknown): string => {
    const k = JSON.stringify(body);
    if (!keys.current.has(k)) keys.current.set(k, newIdempotencyKey());
    return keys.current.get(k)!;
  };

  const isNew = sub.kind === "NewPatient";
  const matches = refusal?.duplicates ?? sub.duplicateMatches;
  const exact = matches.find((m) => m.rule === "ExactId") ?? null;
  const probable = matches.some((m) => m.rule === "SurnameDob" || m.rule === "SurnameMobile");
  const creating = isNew && target === "new";
  const needsReason = creating && exact !== null;
  const needsAck = creating && !exact && probable;
  const correctionPaths = (refusal?.errors ?? []).map((e) => e.path).filter(correctable);

  // A scheme typed as "Other / not listed" (no scheme id, a name typed) cannot be approved as typed: the reviewer chooses it from
  // the platform's lists, and so does a reviewer whose approval was refused on the scheme, plan or option (discontinued, missing).
  const ma = sub.payload.medicalAid;
  const typedNames = ma.hasMedicalAid === false || ma.sameAsMainAccount === true || str(ma.schemeId) ? [] : [str(ma.schemeName), str(ma.planName), str(ma.optionName)].filter((n): n is string => n !== null);
  const pickerErrors = (refusal?.errors ?? []).filter((e) => PICKED.test(e.path));
  const showPicker = typedNames.length > 0 || pickerErrors.length > 0;
  const schemes = useQuery({ queryKey: ["reference", "schemes"], queryFn: () => reference.schemes(), enabled: showPicker, staleTime: 5 * 60_000 });
  const plans = useQuery({ queryKey: ["reference", "plans", pick.schemeId], queryFn: () => reference.plans(pick.schemeId), enabled: showPicker && pick.schemeId !== "", staleTime: 5 * 60_000 });
  const options = useQuery({ queryKey: ["reference", "options", pick.planId], queryFn: () => reference.options(pick.planId), enabled: showPicker && pick.planId !== "", staleTime: 5 * 60_000 });
  // The scheme, plan and option the patient chose from the list, by name, so the reviewer can check them against the card photos
  // (testing of 12 Oct 2026: "Chosen from the list" before). The same cached reference reads as the picker's.
  const sentIds = { schemeId: str(ma.schemeId), planId: str(ma.planId), optionId: str(ma.optionId) };
  const sentSchemes = useQuery({ queryKey: ["reference", "schemes"], queryFn: () => reference.schemes(), enabled: !!sentIds.schemeId, staleTime: 5 * 60_000 });
  const sentPlans = useQuery({ queryKey: ["reference", "plans", sentIds.schemeId ?? ""], queryFn: () => reference.plans(sentIds.schemeId!), enabled: !!sentIds.schemeId && !!sentIds.planId, staleTime: 5 * 60_000 });
  const sentOptions = useQuery({ queryKey: ["reference", "options", sentIds.planId ?? ""], queryFn: () => reference.options(sentIds.planId!), enabled: !!sentIds.planId && !!sentIds.optionId, staleTime: 5 * 60_000 });
  const nameOf = (id: string | null, q: { isPending: boolean; data?: { data: { items: { id: string; name: string }[] } } | undefined }): ReactNode =>
    !id ? dash : q.data?.data.items.find((r) => r.id === id)?.name ?? (q.isPending ? <span className="faint">…</span> : <span className="faint">not in the list</span>);
  const schemeRows = (schemes.data?.data.items ?? []).filter((r) => r.inEffect);
  const planRows = (plans.data?.data.items ?? []).filter((r) => r.inEffect);
  const optionRows = (options.data?.data.items ?? []).filter((r) => r.inEffect);
  // The reference rules the approval applies (UC-PATIENTS-015 E1): a plan when the scheme lists plans, an option when the plan lists options.
  const picked = !showPicker || (pick.schemeId !== "" && plans.isSuccess && (planRows.length === 0 ? pick.planId === "" : pick.planId !== "" && options.isSuccess && (optionRows.length === 0 || pick.optionId !== "")));
  const ready = !busy && block === null && (!needsReason || reason.trim() !== "") && (!needsAck || acknowledged) && picked && decidedAll;

  const body = (): RegistrationApproveInput => {
    const out: RegistrationApproveInput = {};
    if (isNew && target !== "new") out.useExistingPatientId = target as RegistrationApproveInput["useExistingPatientId"];
    if (needsReason) out.duplicateOverride = { ofPatientId: exact!.id, reason: reason.trim() };
    if (needsAck) out.duplicateAcknowledged = true;
    let nested: Record<string, unknown> = {};
    for (const [path, value] of Object.entries(corrections)) if (value.trim() !== "") nested = setPath(nested, path, value.trim());
    if (showPicker && pick.schemeId) for (const [k, v] of Object.entries(pick)) if (v) nested = setPath(nested, `medicalAid.${k}`, v);
    if (Object.keys(nested).length > 0) out.corrections = nested as RegistrationApproveInput["corrections"];
    if (recordChanges) {
      const fields = recordChanges.changes.map((c) => c.field);
      out.recordChanges = { patientVersion: recordChanges.patientVersion, keep: fields.filter((f) => decisions[f] === "keep"), take: fields.filter((f) => decisions[f] === "take") };
    }
    return out;
  };

  const approve = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const b = body();
      const { data } = await registrations.approve(sub.id, b, keyFor(b));
      invalidate(qc, data.patientId);
      onDone(<Alert tone="ok"><b>{data.created ? `Registered ${submissionName(sub)} as ${data.accountCode}.` : `Updated ${data.accountCode} from the kiosk.`}</b> {data.consentRecordId ? "The consent is on the record." : "No consent was recorded: ask for it on the current wording."} <Link to={`/patients/${data.patientId}`}>Open the record</Link></Alert>);
    } catch (err) {
      const changed = isApiError(err) && err.code === "VALIDATION_FAILED" ? err.errors.find((e) => e.path.startsWith("recordChanges")) : undefined;
      if (changed && isApiError(err) && err.meta.recordChanges) {
        // E7: the record changed (again) since the review read it — show the changes as they are now and ask for a decision on each.
        setListed(err.meta.recordChanges as RegistrationRecordChanges);
        if (changed.code === "stale") setDecided(null);
        setFailure(<Alert tone="warn"><b>{changed.code === "stale" ? "The record changed again while you were deciding." : "The record changed after the patient sent this."}</b> Decide, for each change below, whether to keep the record's value or take the patient's.</Alert>);
      } else if (isApiError(err) && err.code === "VALIDATION_FAILED" && err.errors.length > 0) {
        const dup = Array.isArray(err.meta.duplicates) ? (err.meta.duplicates as DuplicateMatch[]) : null;
        setRefusal({ errors: err.errors.map((e) => ({ path: e.path, message: e.message })), duplicates: dup ?? refusal?.duplicates ?? null });
      } else if (isApiError(err) && err.code === "ILLEGAL_TRANSITION") {
        invalidate(qc);
        setFailure(<Alert tone="warn"><b>Someone else has already reviewed this submission.</b> It is {String(err.meta.from ?? "closed").toLowerCase()}.</Alert>);
      } else setFailure(<Alert tone="crit"><b>Could not approve.</b> {describeError(err)}</Alert>);
    } finally {
      setBusy(false);
    }
  };
  const reject = async () => {
    if (rejectReason.trim() === "") return;
    setBusy(true);
    setFailure(null);
    try {
      await registrations.reject(sub.id, rejectReason.trim(), keyFor({ reject: rejectReason.trim() }));
      invalidate(qc);
      onDone(<Alert tone="ok"><b>Rejected the submission from {submissionName(sub)}.</b> Nothing on a patient record changed.</Alert>);
    } catch (err) {
      setFailure(<Alert tone="crit"><b>Could not reject.</b> {describeError(err)}</Alert>);
    } finally {
      setBusy(false);
    }
  };

  const d = sub.payload.details;
  const m = sub.payload.medicalAid;
  const address = d.address && typeof d.address === "object" ? d.address as Record<string, unknown> : {};
  const hasHistory = "medicalHistory" in d;
  const granted = sub.payload.consent.purposes.filter((p) => p.granted).map((p) => `${CONSENT_PURPOSE_LABELS[p.purpose as ConsentPurpose] ?? p.purpose}${p.channel ? ` (${p.channel})` : ""}`);
  const declined = sub.payload.consent.purposes.filter((p) => !p.granted).map((p) => CONSENT_PURPOSE_LABELS[p.purpose as ConsentPurpose] ?? p.purpose);
  const generalErrors = (refusal?.errors ?? []).filter((e) => !correctable(e.path));

  return (
    <Modal wide title={`${kindLabel(sub)} · ${submissionName(sub)}`} meta={`sent ${formatDateTime(sub.submittedAt)}${sub.openedBy ? ` · handed over by ${sub.openedBy.displayName}` : ""}`} onClose={onClose}
      onSubmit={() => { if (rejecting) void reject(); else if (ready) void approve(); }}
      footer={rejecting ? <>
        <button className="btn" type="button" onClick={() => setRejecting(false)} disabled={busy}>Back</button>
        <button className="btn danger" type="submit" disabled={busy || rejectReason.trim() === "" || block !== null} title={block ? WRITE_BLOCK_TITLE[block] : undefined}>{busy ? "Rejecting…" : "Reject submission"}</button>
      </> : <>
        <button className="btn" type="button" onClick={onClose} disabled={busy}>Close</button>
        <button className="btn" type="button" onClick={() => setRejecting(true)} disabled={busy}>Reject…</button>
        <button className="btn primary" type="submit" disabled={!ready} title={block ? WRITE_BLOCK_TITLE[block] : undefined}>{busy ? "Approving…" : creating ? "Approve and register" : "Approve"}</button>
      </>}>
      {failure}
      {!isNew && sub.existingPatientId && <div className="help">Applies to the record of <Link to={`/patients/${sub.existingPatientId}`}>{submissionName(sub)}</Link>; only what changed is written, and the audit names the submission.</div>}
      <div className="grid-2">
        <div className="stack gap-8">
          <div className="small strong muted">Details as typed</div>
          <KV rows={[
            ["Name", [str(d.title), str(d.firstNames), str(d.surname)].filter(Boolean).join(" ") || dash],
            ["ID", str(d.idNumber) ? <span className="mono">{str(d.idNumber)}{str(d.idCountryOfIssue) ? ` · ${str(d.idCountryOfIssue)}` : ""}</span> : "None given"],
            ["Date of birth", str(d.dateOfBirth) ? formatDate(`${str(d.dateOfBirth)}T12:00:00.000Z`) : "From the ID number"],
            ["Mobile", str(d.mobile) ?? dash], ["Email", str(d.email) ?? dash], ["Preferred channel", str(d.preferredChannel) ?? dash],
            ["Address", [str(address.line1), str(address.suburb), str(address.city), str(address.postalCode)].filter(Boolean).join(", ") || dash],
            ...(hasHistory ? [["Medical history", str(d.medicalHistory) ?? <span className="faint">not shown to your role</span>] as [ReactNode, ReactNode]] : []),
          ]} />
        </div>
        <div className="stack gap-8">
          <div className="small strong muted">Medical aid</div>
          {m.hasMedicalAid === false ? <span className="help">No medical aid — a cash patient.</span> : <KV rows={[
            ["Scheme", str(m.schemeName) ?? nameOf(sentIds.schemeId, sentSchemes)], ["Plan", str(m.planName) ?? nameOf(sentIds.planId, sentPlans)], ["Option", str(m.optionName) ?? nameOf(sentIds.optionId, sentOptions)],
            ["Member number", str(m.membershipNumber) ? <span className="mono">{str(m.membershipNumber)}</span> : dash], ["Dependant code", str(m.dependentCode) ? <span className="mono">{str(m.dependentCode)}</span> : dash],
          ]} />}
          {showPicker && (
            <fieldset className="stack gap-8">
              <legend className="small strong muted">Scheme from the list</legend>
              <span className="help">{typedNames.length > 0 ? <>The patient's scheme is not in the list: they typed “{typedNames.join(" · ")}”. Choose it from the list to approve.</> : "Choose the scheme, plan and option from the list."}</span>
              <Field label="Scheme" required htmlFor="reg-scheme" error={pickerErrors.find((e) => e.path === "medicalAid.schemeId")?.message}>
                <Select id="reg-scheme" value={pick.schemeId} disabled={schemes.isPending} onChange={(e) => setPick({ ...NO_PICK, schemeId: e.target.value })}>
                  <option value="">{schemes.isPending ? "Loading…" : "—"}</option>
                  {schemeRows.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </Select>
              </Field>
              <Field label="Plan" required={planRows.length > 0} htmlFor="reg-plan" error={pickerErrors.find((e) => e.path === "medicalAid.planId")?.message}>
                <Select id="reg-plan" value={pick.planId} disabled={pick.schemeId === "" || plans.isPending} onChange={(e) => setPick((p) => ({ ...p, planId: e.target.value, optionId: "" }))}>
                  <option value="">{pick.schemeId !== "" && plans.isSuccess && planRows.length === 0 ? "No plans listed" : "—"}</option>
                  {planRows.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </Select>
              </Field>
              <Field label="Option" required={optionRows.length > 0} htmlFor="reg-option" error={pickerErrors.find((e) => e.path === "medicalAid.optionId")?.message}>
                <Select id="reg-option" value={pick.optionId} disabled={pick.planId === "" || options.isPending} onChange={(e) => setPick((p) => ({ ...p, optionId: e.target.value }))}>
                  <option value="">{pick.planId !== "" && options.isSuccess && optionRows.length === 0 ? "No options listed" : "—"}</option>
                  {optionRows.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </Select>
              </Field>
              {schemes.isError && <ErrorAlert error={schemes.error} />}
            </fieldset>
          )}
          <div className="small strong muted">Consent</div>
          <KV rows={[
            ["Wording", `version ${sub.payload.consent.textVersion}`],
            ["Signed by", `${sub.payload.consent.signerName ?? "—"}${sub.payload.consent.signerType === "Guardian" ? " (parent or guardian)" : ""}${sub.payload.consent.hasSignature ? "" : " · no signature"}`],
            ["Agreed to", granted.join(", ") || dash], ["Declined", declined.join(", ") || dash],
          ]} />
          {sub.consentRecordId && <span className="help">The consent was recorded on the kiosk already; approving does not record it again.</span>}
        </div>
      </div>

      {recordChanges && <RecordChanges name={submissionName(sub)} changes={recordChanges} decisions={decisions} onDecide={decide} />}

      {sub.status === "Pending" && <CapturedImages q={detail} />}

      {isNew && matches.length > 0 && (
        <fieldset className="stack gap-8">
          <legend className="small strong muted">Possible duplicates</legend>
          <label className="check"><input type="radio" name="reg-target" checked={target === "new"} onChange={() => setTarget("new")} /><span className={`box radio${target === "new" ? " on" : ""}`} />Register as a new patient</label>
          {matches.map((x) => (
            <label key={x.id} className="check">
              <input type="radio" name="reg-target" checked={target === x.id} onChange={() => setTarget(x.id)} /><span className={`box radio${target === x.id ? " on" : ""}`} />
              <span>Apply to <Link to={`/patients/${x.id}`} target="_blank" rel="noopener">{x.displayName}</Link> <span className="mono">{x.accountCode}</span> · born {formatDate(`${x.dateOfBirth}T12:00:00.000Z`)} · <Chip tone={x.rule === "ExactId" ? "crit" : x.rule === "FirstNamesSurname" ? "neutral" : "warn"}>{RULE_LABEL[x.rule]}</Chip></span>
            </label>
          ))}
          {needsReason && <Field label={`Why register again when ${exact!.displayName} (${exact!.accountCode}) has this ID number?`} required htmlFor="reg-override" help="Stored with the new record and audited (D42)"><TextInput id="reg-override" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} /></Field>}
          {needsAck && <Checkbox checked={acknowledged} onChange={setAcknowledged}>I have checked these matches; this is a different person</Checkbox>}
        </fieldset>
      )}

      {refusal && (generalErrors.length > 0 || correctionPaths.length > 0) && (
        <Alert tone="crit" role="alert"><b>The patient rules refused this approval.</b> {refusal.errors.map((e) => e.message).join(" ")}</Alert>
      )}
      {correctionPaths.length > 0 && (
        <div className="stack gap-8">
          <div className="small strong muted">Correct and approve again</div>
          {correctionPaths.map((path) => (
            <Field key={path} label={`Corrected ${fieldLabel(path)}`} htmlFor={`reg-fix-${path}`} error={refusal!.errors.find((e) => e.path === path)?.message}>
              <TextInput id={`reg-fix-${path}`} value={corrections[path] ?? ""} maxLength={200} onChange={(e) => setCorrections((c) => ({ ...c, [path]: e.target.value }))} />
            </Field>
          ))}
          <span className="help">Corrections are merged over what the patient typed and audited as yours.</span>
        </div>
      )}

      {rejecting && (
        <Field label="Why is this submission rejected?" required htmlFor="reg-reject" help="Nothing on a patient record changes. The images the kiosk captured are deleted 30 days later.">
          <TextArea id="reg-reject" value={rejectReason} maxLength={500} rows={2} onChange={(e) => setRejectReason(e.target.value)} />
        </Field>
      )}
    </Modal>
  );
}
